import { useState, useEffect } from "react";
import axios from "axios";
import styles from "./AddNewCar.module.css";

import BasicInfo from "../../components/steps/BasicInfo/BasicInfo";
import Specifications from "../../components/steps/Specifications/Specifications";
import Pricing from "../../components/steps/Pricing/Pricing";
import Features from "../../components/steps/Features/Features";
import Images from "../../components/steps/Images/Images";
import OwnerInfo from "../../components/steps/OwnerInfo/OwnerInfo";

import CommonButton from "../../components/ui/CommonButton/CommonButton";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { env } from "../../constants/env";

const steps = [
  "Basic info",
  "Specifications",
  "Pricing",
  "Features",
  "Images",
  "Owner",
];

const AddNewCar = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    make: "",
    model: "",
    year: "",
    color: "",
    licensePlate: "",
    category: "",
    type: "",
    tier: "Standard",
    transmission: "",
    fuelType: "",
    numberOfSeats: "4",
    engineSize: "2.0",
    mileage: "",
    localDailyRate: "",
    outsideCityDailyRate: "",
    availability: "Available",
    features: {
      comfortAndConvenience: {},
      safety: {},
    },
    images: [],
    description: "",
    owner: {
      name: "",
      email: "",
      address: "",
      phoneNumber: "",
      latitude: "",
      longitude: "",
      openingTime: "",
      closingTime: "",
      emailGreeting: "",
      pickupInstructions: "",
      emailNote: "",
    },
    points: 0,
    insurancePercentage: 0,
  });

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const res = await axios.get(`${env.apiUrl}/api/cars/${id}`);
        const car = res.data.car;

        setFormData({
          make: car.make || "",
          model: car.model || "",
          year: car.year || "",
          color: car.color || "",
          licensePlate: car.licensePlate || "",
          category: car.category || "",
          type: car.type || "",
          tier: car.tier || "Standard",
          transmission: car.transmission || "",
          fuelType: car.fuelType || "",
          numberOfSeats: car.numberOfSeats || "4",
          engineSize: car.engineSize || "2.0",
          mileage: car.currentMileage || "",
          localDailyRate: car.localDailyRate || "",
          outsideCityDailyRate: car.outsideCityDailyRate || "",
          availability: car.availabilityStatus || "Available",
          features: {
            comfortAndConvenience: car.features?.comfortAndConvenience || {},
            safety: car.features?.safety || {},
          },
          images: car.images || [],
          description: car.description || "",
          owner: {
            name: car.owner?.name || "",
            email: car.owner?.email || "",
            address: car.owner?.address || "",
            phoneNumber: car.owner?.phoneNumber || "",
            latitude: car.owner?.latitude || "",
            longitude: car.owner?.longitude || "",
            openingTime: car.owner?.openingTime || "",
            closingTime: car.owner?.closingTime || "",
            emailGreeting: car.owner?.emailGreeting || "",
            pickupInstructions: car.owner?.pickupInstructions || "",
            emailNote: car.owner?.emailNote || "",
          },
          points: car.points || 0,
          insurancePercentage: car.insurancePercentage || 0,
        });

        setIsEditing(true);
      } catch (err) {
        toast.error("❌ Failed to load car for editing.");
        console.error("Load error:", err);
      }
    };

    if (id) fetchCar();
  }, [id]);

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      try {
        setIsSubmitting(true);
        const form = new FormData();

        for (const key in formData) {
          if (key === "images") {
            formData.images.forEach((img) => {
              if (typeof img === "object") form.append("images", img);
            });
          } else if (key === "features" || key === "owner") {
            form.append(key, JSON.stringify(formData[key]));
          } else if (key === "availability") {
            // Backend expects "availabilityStatus"
            form.append("availabilityStatus", formData[key]);
          } else {
            form.append(key, formData[key]);
          }
        }

        const url = isEditing
          ? `${env.apiUrl}/api/cars/${id}`
          : `${env.apiUrl}/api/cars`;

        const method = isEditing ? axios.put : axios.post;

        const res = await method(url, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        toast.success(
          `✅ Car ${isEditing ? "updated" : "added"} successfully!`
        );
        console.log("Saved Car:", res.data);
        navigate("/cars");
      } catch (err) {
        console.error("❌ Error:", err);
        toast.error(
          `Failed to ${isEditing ? "update" : "add"} car. See console.`
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const updateFormData = (newData) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const renderStep = () => {
    const stepProps = { formData, updateFormData };
    switch (currentStep) {
      case 0:
        return <BasicInfo {...stepProps} />;
      case 1:
        return <Specifications {...stepProps} />;
      case 2:
        return <Pricing {...stepProps} />;
      case 3:
        return <Features {...stepProps} />;
      case 4:
        return <Images {...stepProps} />;
      case 5:
        return <OwnerInfo {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.sidebar}>
        <ul>
          {steps.map((step, i) => (
            <li key={i} className={i === currentStep ? styles.active : ""}>
              {step}
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.content}>{renderStep()}</div>

      <div className={styles.actions}>
        <div>
          {currentStep > 0 && (
            <CommonButton onClick={handlePrev} title="Previous" outlined />
          )}
        </div>
        <CommonButton
          onClick={handleNext}
          title={
            isSubmitting
              ? isEditing
                ? "Updating..."
                : "Submitting..."
              : currentStep === steps.length - 1
              ? isEditing
                ? "Update"
                : "Submit"
              : "Next"
          }
          disabled={
            isSubmitting ||
            (currentStep === 0
              ? formData.make === "" ||
                formData.model === "" ||
                formData.year === "" ||
                formData.color === "" ||
                formData.licensePlate === ""
              : currentStep === 1
              ? formData.category === "" ||
                formData.type === "" ||
                formData.tier === "" ||
                formData.transmission === "" ||
                formData.fuelType === "" ||
                formData.numberOfSeats === "" ||
                formData.engineSize === ""
              : currentStep === 2
              ? formData.localDailyRate === "" || formData.availability === ""
              : currentStep === 4
              ? formData.images.length <= 0
              : currentStep === 5 &&
                (formData.owner.name === "" || formData.owner.email === ""))
          }
        />
      </div>
    </div>
  );
};

export default AddNewCar;
